import { Box, VStack, Text, Flex, Separator, Checkbox } from '@chakra-ui/react';
import { ReactElement, ReactNode, useId } from 'react';

type OptionWithSecondary = {
  value: string;
  label: string;
  secondaryText?: string;
};

type MultiSelectListProps = {
  options: OptionWithSecondary[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  emptyMessage?: ReactNode;
};

type MultiSelectOptionRowProps = {
  option: OptionWithSecondary;
  isSelected: boolean;
  onToggle: (value: string) => void;
  showSeparator: boolean;
};

/**
 * One row per mount so `useId()` is unique. Required because {@link MultiSelectField} wraps the
 * control in `Field.Root`; Ark `Checkbox` would otherwise reuse `field.ids.control` for every
 * hidden input → duplicate DOM ids → every label activates the first checkbox.
 */
const MultiSelectOptionRow = ({
  option,
  isSelected,
  onToggle,
  showSeparator,
}: MultiSelectOptionRowProps): ReactElement => {
  const hiddenInputId = useId();
  return (
    <Box>
      <Checkbox.Root
        checked={isSelected}
        ids={{ hiddenInput: hiddenInputId }}
        onCheckedChange={() => onToggle(option.value)}
        colorPalette="green"
        size="lg"
        px="15px"
        py="s"
        cursor="pointer"
        _hover={{ bg: 'background.canvas' }}
        width="100%"
      >
        <Checkbox.HiddenInput />
        <Flex alignItems="center" gap="15px">
          <Checkbox.Control>
            <Checkbox.Indicator />
          </Checkbox.Control>
          <Checkbox.Label flex="1">
            <VStack gap="3xs" align="flex-start">
              <Text textStyle="body.s.regular" color="text.primary" lineHeight="1.3">
                {option.label}
              </Text>
              {option.secondaryText && (
                <Text textStyle="body.xs.regular" color="text.tertiary" lineHeight="1.3">
                  {option.secondaryText}
                </Text>
              )}
            </VStack>
          </Checkbox.Label>
        </Flex>
      </Checkbox.Root>
      {showSeparator && <Separator />}
    </Box>
  );
};

/**
 * MultiSelectList component - Displays a scrollable list of options with checkboxes
 * Used within MultiSelectInput dropdown
 */
export const MultiSelectList = ({
  options,
  selectedValues,
  onToggle,
  emptyMessage = 'Keine Ergebnisse gefunden',
}: MultiSelectListProps): ReactElement => {
  if (options.length === 0) {
    return (
      <Box px="15px" py="s">
        <Text textStyle="body.s.regular" color="text.tertiary" textAlign="center">
          {emptyMessage}
        </Text>
      </Box>
    );
  }

  return (
    <VStack gap="0" align="stretch">
      {options.map((option, index) => (
        <MultiSelectOptionRow
          key={option.value}
          option={option}
          isSelected={selectedValues.includes(option.value)}
          onToggle={onToggle}
          showSeparator={index < options.length - 1}
        />
      ))}
    </VStack>
  );
};
